import { Box, Flex, Text } from '@chakra-ui/react';
import { OutlineCard, Table } from '@morressier/alembic';
import EditResearchFocusAndExpertiseModal from './EditResearchFocusAndExpertiseModal';
import { formatDate } from 'src/utils/date';
import { RenderSectionDetails } from './RenderSectionDetails';
import {
  IResearchFocusAndExpertiseType,
  useUserDetailsData
} from '../UserRegistrationSteps/StepThree/signupHook';

export const fetchConfigValue = (config: any, value: string) => {
  const optionValue = config?.params?.labels.options[String(value)]?.main;
  return {
    optionValue
  };
};

const getConfigLabel = (config: any, fallback: string) =>
  config?.params?.labels?.main ?? fallback;

const ResearchFocusAndExpertiseForm = ({
  organizationId,
  data: { researchFocusAndExpertise }
}: {
  organizationId: string;
  data: { researchFocusAndExpertise: IResearchFocusAndExpertiseType };
}) => {
  const { configs } = useUserDetailsData(organizationId);
  const { optionValue } = fetchConfigValue(
    configs.ReviewerInterest,
    researchFocusAndExpertise?.reviewerInterest
  );
  const hasRowLevelScandinavianKeywords =
    researchFocusAndExpertise?.specialtyAndKeywords?.some(
      item => item.scandinavianKeyword
    );
  const tableMinWidth =
    configs.SubSpecialty || configs.ScandinavianKeyword ? '960px' : '560px';

  return (
    <Flex direction="column" width="100%" gap={3}>
      <Flex align={'center'} justifyContent={'space-between'}>
        <Text textStyle="headingGroup">Research Interest</Text>
        <EditResearchFocusAndExpertiseModal
          organizationId={organizationId}
          data={{ researchFocusAndExpertise }}
        />
      </Flex>

      <OutlineCard width="100%" padding={4}>
        <Flex direction={'column'} gap={3}>
          <Flex direction="column" gap={1}>
            {configs.Specialty && configs.Keyword && (
              <>
                <Text textStyle="headingItem">
                  Research Specialty & Keyword(s)
                </Text>
                {researchFocusAndExpertise?.specialtyAndKeywords?.length ? (
                  <Box overflowX="auto">
                    <Box minWidth={tableMinWidth}>
                      <Table data-cy="table">
                        <Table.HeaderRow
                          display="flex"
                          data-cy="table-header"
                          gap={4}
                        >
                          <Table.HeaderCell width={20}>
                            Sr. No.
                          </Table.HeaderCell>
                          <Table.HeaderCell flex={1} textAlign="left">
                            {getConfigLabel(configs.Specialty, 'Specialty')}
                          </Table.HeaderCell>
                          {configs.SubSpecialty && (
                            <Table.HeaderCell flex={1} textAlign="left">
                              {getConfigLabel(
                                configs.SubSpecialty,
                                'Sub-specialty'
                              )}
                            </Table.HeaderCell>
                          )}
                          <Table.HeaderCell flex={1} textAlign="left">
                            {getConfigLabel(configs.Keyword, 'Keyword(s)')}
                          </Table.HeaderCell>
                          {configs.ScandinavianKeyword && (
                            <Table.HeaderCell flex={1} textAlign="left">
                              {getConfigLabel(
                                configs.ScandinavianKeyword,
                                'Scandinavian Keyword(s)'
                              )}
                            </Table.HeaderCell>
                          )}
                        </Table.HeaderRow>

                        <Table.Body data-cy="table-body">
                          {researchFocusAndExpertise?.specialtyAndKeywords?.map(
                            (data, index) => (
                              <Table.Row
                                key={data.id}
                                alignItems="start"
                                _last={{ borderBottom: 'none' }}
                                data-cy="table-row"
                                display="flex"
                                gap={4}
                              >
                                <Table.RowCell width={20}>
                                  {index + 1}
                                </Table.RowCell>
                                <Table.RowCell flex={1} data-cy="header-cell">
                                  {data.specialtyName || '-'}
                                </Table.RowCell>
                                {configs.SubSpecialty && (
                                  <Table.RowCell flex={1} data-cy="header-cell">
                                    {data.subSpecialtyName || '-'}
                                  </Table.RowCell>
                                )}
                                <Table.RowCell flex={1} data-cy="header-cell">
                                  {data.keyword || '-'}
                                </Table.RowCell>
                                {configs.ScandinavianKeyword && (
                                  <Table.RowCell flex={1} data-cy="header-cell">
                                    {data.scandinavianKeyword || '-'}
                                  </Table.RowCell>
                                )}
                              </Table.Row>
                            )
                          )}
                        </Table.Body>
                      </Table>
                    </Box>
                  </Box>
                ) : (
                  <Text textStyle="textBody" color="GrayText">
                    No data available
                  </Text>
                )}
              </>
            )}
          </Flex>
          {configs.ProfessionalInterests && (
            <RenderSectionDetails
              label={getConfigLabel(
                configs?.ProfessionalInterests,
                'Professional Interests'
              )}
              value={researchFocusAndExpertise?.professionalInterests}
            />
          )}
          <RenderSectionDetails
            label="Reviewer Interest"
            value={optionValue || ''}
          />
          {configs.ScandinavianKeyword &&
            !hasRowLevelScandinavianKeywords &&
            researchFocusAndExpertise?.scandinavianKeywords && (
              <RenderSectionDetails
                label="Scandinavian Keyword(s)"
                value={researchFocusAndExpertise.scandinavianKeywords}
              />
            )}{' '}
          {researchFocusAndExpertise?.reviewerInterest === 'Yes' &&
            researchFocusAndExpertise?.unavailabilityPeriodTo &&
            researchFocusAndExpertise?.unavailabilityPeriodFrom && (
              <>
                <RenderSectionDetails
                  label="Unavailable From"
                  value={
                    researchFocusAndExpertise?.unavailabilityPeriodFrom
                      ? formatDate(
                          new Date(
                            researchFocusAndExpertise?.unavailabilityPeriodFrom
                          )
                        )
                      : ''
                  }
                />
                <RenderSectionDetails
                  label="Unavailable To"
                  value={
                    researchFocusAndExpertise?.unavailabilityPeriodTo
                      ? formatDate(
                          new Date(
                            researchFocusAndExpertise?.unavailabilityPeriodTo
                          )
                        )
                      : ''
                  }
                />
              </>
            )}
        </Flex>
      </OutlineCard>
    </Flex>
  );
};

export default ResearchFocusAndExpertiseForm;
